import {
  AfterContentInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  contentChild,
  ContentChild,
  contentChildren,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  Renderer2,
  signal,
  viewChild,
  ViewChild,
} from '@angular/core';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { toObservable } from '@angular/core/rxjs-interop';

import { combineLatest, Subject } from 'rxjs';
import { delay, filter, map, takeUntil, tap } from 'rxjs/operators';

import { NEW_DESIGN } from '@kros-sk/core/design-version';
import { TooltipDirective, TooltipPosition } from '@kros-sk/ui/tooltip';

import { KROS_INPUT_ERROR } from './kros-error.directive';
import { KROS_FORM_FIELD_HOST } from './kros-form-field.token';
import { KrosFormFieldControl } from './kros-form-field-control';
import { KrosInputHint } from './kros-hint.directive';
import { KrosLabel } from './kros-label.directive';

let nextUniqueId = 0;

@Component({
  selector: 'kros-form-field',
  templateUrl: './kros-form-field.component.html',
  styleUrls: ['./kros-form-field.component.scss'],
  host: {
    class: 'kros-form-field',
    '[class.kros-form-field-disabled]': 'control?.disabled',
    '[class.kros-form-field-invalid]': 'control?.errorState',
    '[class.ng-untouched]': 'control?.ngControl?.untouched || control?.ngrxControl?.state?.isUntouched',
    '[class.ng-touched]': 'control?.ngControl?.touched || control?.ngrxControl?.state?.isTouched',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: KROS_FORM_FIELD_HOST,
      useExisting: KrosFormFieldComponent,
    },
  ],
})
export class KrosFormFieldComponent implements AfterContentInit, OnDestroy {
  elementRef = inject(ElementRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly renderer = inject(Renderer2);
  private readonly scrollDispatcher = inject(ScrollDispatcher);
  protected readonly newDesign = inject(NEW_DESIGN, { optional: true });

  readonly labelId = `kros-form-field-label-${nextUniqueId++}`;

  hasLabel = false;
  hintTooltipPlacement: TooltipPosition = 'above';

  @ContentChild(KrosLabel) _labelChild: KrosLabel | undefined;
  @ContentChild(KrosFormFieldControl) control!: KrosFormFieldControl<any>;
  readonly hintChild = contentChild(KrosInputHint);
  readonly hintChild$ = toObservable(this.hintChild);
  readonly errorChildren = contentChildren(KROS_INPUT_ERROR, { descendants: true });
  readonly errorChildren$ = toObservable(this.errorChildren);
  readonly hint = signal<string>('');

  @ViewChild('tooltip', { static: false }) tooltip!: TooltipDirective;
  @ViewChild('inputContainer', { static: false }) inputContainer!: ElementRef<HTMLElement>;

  suffixContainer = viewChild<ElementRef>('suffixContainer');

  hasSuffixContent = computed(() => {
    const container = this.suffixContainer()?.nativeElement;
    return container && (container.textContent?.trim() !== '' || container.children.length > 0);
  });

  @Input({ transform: booleanAttribute }) showErrorsWhenUntouched = false;
  @Input({ transform: booleanAttribute }) allowClickableIcon = false;

  private destroyed = new Subject<void>();

  ngAfterContentInit(): void {
    this.hasLabel = !!this._labelChild;
    this.initializeControl();
    this.initializeTooltip();
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  get inputContainerWidth(): number {
    return this.inputContainer?.nativeElement?.clientWidth || 0;
  }
  // TODO: added warning message handling
  getDisplayedMessages(): 'error' | 'hint' | 'warning' {
    const hasErrors = this.errorChildren()?.length > 0;
    const isTouched = this.control?.ngControl
      ? this.control.ngControl.touched
      : this.control?.ngrxControl
        ? this.control.ngrxControl.state.isTouched
        : false;

    if (hasErrors && (this.showErrorsWhenUntouched || isTouched)) {
      return 'error';
    }
    return 'hint';
  }

  getTooltipClass(): string {
    if (this.getDisplayedMessages() === 'error' && this.newDesign) {
      return 'error-tooltip';
    }
    if (this.getDisplayedMessages() === 'warning' && this.newDesign) {
      return 'warning-tooltip';
    }
    return '';
  }

  setHint(hintContent: string): void {
    this.hint.set(hintContent ?? '');
  }

  protected canShowErrorIcon(): boolean {
    return this.hasErrorToShow();
  }

  showTooltip(): void {
    if (this.canShowTooltip()) {
      this.tooltip.show();
    }
  }

  closeTooltip(): void {
    if (this.canCloseTooltip()) {
      setTimeout(() => {
        this.tooltip.hide();
      });
    }
  }

  addClass(className: string): void {
    this.renderer.addClass(this.elementRef.nativeElement, className);
  }

  removeClass(className: string): void {
    this.renderer.removeClass(this.elementRef.nativeElement, className);
  }

  addStyle(styleName: string, value: string): void {
    if (!this.control) return;
    this.renderer.setStyle(this.control.elementRef.nativeElement, styleName, value);
  }

  removeStyle(styleName: string): void {
    if (!this.control) return;
    this.renderer.removeStyle(this.control.elementRef.nativeElement, styleName);
  }

  private canShowTooltip(): boolean {
    return !this.tooltip.isTooltipVisible() && (this.hasErrorToShow() || (this.control && !this.control.focused && this.hasHintToShow()));
  }

  private hasHintToShow(): boolean {
    return !!this.hintChild()?.nativeElement.textContent?.trim() || !!this.hint().trim();
  }

  private hasErrorToShow(): boolean {
    return (
      this.getDisplayedMessages() === 'error' &&
      this.errorChildren()?.length > 0 &&
      this.errorChildren().some((error) => error.nativeElement.textContent.trim())
    );
  }

  private canCloseTooltip(): boolean {
    return this.getDisplayedMessages() !== 'error' || !this.control || !this.control.focused;
  }

  private initializeTooltip(): void {
    const hintChild = this.hintChild();

    if (hintChild) {
      this.hintTooltipPlacement = hintChild.align;
    }

    if (!this.control) return;

    this.scrollDispatcher
      .scrolled()
      .pipe(
        takeUntil(this.destroyed),
        filter(() => this.tooltip.isTooltipVisible()),
        tap(() => this.tooltip.hide())
      )
      .subscribe();

    const controlChanges$ = combineLatest([this.hintChild$, this.errorChildren$, this.control.focusChanges]).pipe(
      // Force next tick to ensure error.nativeElement.textContent is set in hasErrorToShow() so error tooltip can be shown.
      delay(0),
      map(([, , focused]) => focused && this.canShowTooltip()),
      takeUntil(this.destroyed)
    );

    controlChanges$.subscribe((resp) => (resp ? this.showTooltip() : this.closeTooltip()));
  }

  private initializeControl(): void {
    if (!this.control) return;

    const control = this.control;

    control.stateChanges.pipe(takeUntil(this.destroyed)).subscribe(() => this.cdRef.markForCheck());

    if (control.ngControl && control.ngControl.valueChanges) {
      control.ngControl.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(() => this.cdRef.markForCheck());
    }
  }
}
